import React, { useRef } from "react"
import cx from "classnames"
import PropTypes from "prop-types"
import { MenuButton, MenuPopover, Menu, MenuItems } from "@reach/menu-button"
import Icon from "@kit/design/icon"
import analytics from "utils/analytics"
import { slugify } from "core/utils/slugify"

const NavButton = ({ title, isExpanded, onKeyboardActivate }) => {
  function handleClick() {
    // isExpanded reflects state AFTER the click because Reach UI dispatches
    // state updates in handleMouseDown, which fires before onClick runs.
    // So isExpanded === true means the menu just opened.
    if (isExpanded) {
      analytics.track(
        "Element",
        "Clicked",
        {
          element_type: "button",
          name: `nav_section_${slugify(title)}`,
          text: title,
          location: "primary_nav"
        },
        { transport: "beacon" }
      )
    }
  }

  return (
    <MenuButton
      className="nav-hover rounded text-sm mr-1 font-semibold capitalize inline-flex items-center duration-200 focus:outline-none text-black hover:text-black focus:text-black focus:bg-blue-600 focus:bg-warm-white-200"
      style={{ padding: "4px 4px 4px 12px" }}
      onClick={handleClick}
      onKeyDown={e => {
        if (e.key === "Enter" || e.key === " " || e.key === "ArrowDown")
          onKeyboardActivate?.()
      }}
    >
      {title}
      <Icon
        name="down"
        className={cx("transition-transform ease-in-out", {
          "transform -rotate-180": isExpanded
        })}
        aria-hidden
      />
    </MenuButton>
  )
}

/**
 * Roving tabindex: when the menu opens, focus the first menuitem so arrow keys
 * work. We run after a tick so Reach has finished focusing the container and
 * all children are in the DOM.
 */
function focusFirstMenuItem(menuEl) {
  if (!menuEl) return
  const first = menuEl.querySelector("[role=menuitem]")
  if (first) first.focus()
}

export const NavSection = ({ children, title }) => {
  const menuItemsRef = useRef(null)
  const openedByKeyboardRef = useRef(false)

  const colCount = length => {
    if (typeof length !== "number") return
    if (length >= 4) {
      return 2
    } else {
      return 1
    }
  }

  return (
    <Menu className="relative">
      {({ isExpanded }) => {
        if (!isExpanded) openedByKeyboardRef.current = false
        return (
          <>
            <NavButton
              title={title}
              isExpanded={isExpanded}
              onKeyboardActivate={() => {
                openedByKeyboardRef.current = true
              }}
            />

            <MenuPopover style={{ zIndex: 10000 }}>
              {/*
                ref: store container and, when menu opens via keyboard, focus first
                menuitem after a tick so Reach has finished mounting and arrow-key
                nav works. Skip when opened by click to avoid visible focus ring.
                tabIndex={-1}: container is focusable for roving tabindex but not in
                normal tab order.
                Arrow/keyboard handling (ArrowUp, ArrowDown, Home, End) is in
                nav-link.js (handleMenuKeyDown) for a11y menu navigation.
              */}
              <MenuItems
                ref={el => {
                  menuItemsRef.current = el
                  if (el && isExpanded && openedByKeyboardRef.current) {
                    setTimeout(() => focusFirstMenuItem(el), 0)
                  }
                }}
                tabIndex={-1}
                className="rounded-lg shadow-lg main-menu-card text-gray-900 mt-2"
                style={{
                  gridTemplateColumns: `repeat(${colCount(
                    children?.length
                  )}, minmax(340px, 1fr))`
                }}
              >
                {children}
              </MenuItems>
            </MenuPopover>
          </>
        )
      }}
    </Menu>
  )
}

NavSection.propTypes = {
  title: PropTypes.string.isRequired,
  children: PropTypes.oneOfType([PropTypes.element, PropTypes.array])
}

